import { signOut } from '../../api/auth'
import { setLastUsedWorkspaces } from '../../utils'
import { logout } from '../../store/actions/authActions'
import { useDispatch, useSelector } from 'react-redux'
import { useUserInfo } from '../../api/hooks/user/useUserInfo'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '../../router/routes'
import { useTheme } from '../../context/ThemeContext'
import { THEME_IDS, EXPERIMENTAL_THEME_IDS } from '../../styles/themes'
import {
    ScoopMenu,
    ScoopMenuItem,
    ScoopMenuDivider,
    ScoopAvatar,
    ScoopRow,
    ScoopColumn,
    ScoopText,
    ScoopSwitch,
} from '../ui'
import {
    MoonIcon,
    SunIcon,
    GearIcon,
    SignOutIcon,
    ShieldCheckIcon,
    CheckIcon,
    PaletteIcon,
} from '@phosphor-icons/react'
import styles from './AccountDropdown.module.scss'

// List of emails allowed to access dark mode
const ALLOWED_DARK_MODE_EMAILS = [
    'facundo.rivas.if@gmail.com',
    'juanmarengo@sirius.com.ar',
    // Founder/operator — the DI target-state work is explicitly both-themes
    // and needs an operator who can actually see dark mode.
    'brad@scoopanalytics.com',
    'brad@scoop.report',
]

export const AccountDropdown = ({ anchorEl, isMenuOpen, setAnchorEl, workspaceName }) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const userID = useSelector((state) => state.auth.userID)
    const userEmail = useSelector((state) => state.auth.userEmail)

    const { user } = useUserInfo()
    const { currentTheme, setTheme, availableThemes } = useTheme()

    // Check if current user has access to dark mode
    const hasDarkModeAccess = ALLOWED_DARK_MODE_EMAILS.includes(userEmail)

    // Check if current user is a Scoop admin (A1 account only)
    const isScoopAdmin = user?.accountID === 'A1'

    const handleLogout = () => {
        signOut().then(() => {
            setLastUsedWorkspaces(userID, workspaceID, workspaceName)
            dispatch(logout())
        })
    }

    const handleSettings = () => {
        setAnchorEl(null)
        navigate(ROUTES.ACCOUNT)
    }

    const handleScoopAdmin = () => {
        setAnchorEl(null)
        navigate(ROUTES.SCOOPADMIN)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
    }

    const handleThemeToggle = (event) => {
        event.stopPropagation()
        const newTheme = currentTheme === THEME_IDS.DARK ? THEME_IDS.LIGHT : THEME_IDS.DARK
        setTheme(newTheme)
    }

    return (
        <ScoopMenu
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            open={isMenuOpen}
            onClose={handleMenuClose}
            offset={20}
            className={styles.container}
        >
            <ScoopRow className={styles.userInfoContainer}>
                <ScoopAvatar
                    firstName={user?.firstName}
                    lastName={user?.lastName}
                    size="sm"
                    className={styles.userAvatar}
                />
                <ScoopColumn className={styles.userDetails}>
                    <ScoopText className={styles.userName}>
                        {user.firstName + ' ' + user.lastName}
                    </ScoopText>
                    <ScoopText className={styles.userEmail}>{userEmail}</ScoopText>
                </ScoopColumn>
            </ScoopRow>
            <ScoopMenuDivider />
            {hasDarkModeAccess && (
                <ScoopMenuItem
                    onClick={handleThemeToggle}
                    className={styles.themeMenuItem}
                    leftSection={
                        currentTheme === THEME_IDS.DARK ? (
                            <MoonIcon size={18} weight="regular" className={styles.themeIcon} />
                        ) : (
                            <SunIcon size={18} weight="regular" className={styles.themeIcon} />
                        )
                    }
                    rightSection={
                        <ScoopSwitch
                            checked={currentTheme === THEME_IDS.DARK}
                            onChange={handleThemeToggle}
                        />
                    }
                >
                    <ScoopText className={styles.themeText}>Dark mode</ScoopText>
                </ScoopMenuItem>
            )}
            {hasDarkModeAccess && (
                <>
                    <ScoopMenuDivider />
                    <ScoopText className={styles.themeExperimentsLabel}>
                        Theme experiments
                    </ScoopText>
                    {availableThemes
                        .filter((theme) => EXPERIMENTAL_THEME_IDS.includes(theme.id))
                        .map((theme) => (
                            <ScoopMenuItem
                                key={theme.id}
                                onClick={() => setTheme(theme.id)}
                                className={styles.themeMenuItem}
                                leftSection={
                                    <PaletteIcon
                                        size={18}
                                        weight="regular"
                                        className={styles.themeIcon}
                                    />
                                }
                                rightSection={
                                    currentTheme === theme.id ? <CheckIcon size={16} /> : null
                                }
                            >
                                <ScoopText className={styles.themeText}>{theme.name}</ScoopText>
                            </ScoopMenuItem>
                        ))}
                </>
            )}
            {isScoopAdmin && (
                <ScoopMenuItem
                    onClick={handleScoopAdmin}
                    leftSection={
                        <ShieldCheckIcon
                            size={20}
                            weight="regular"
                            className={styles.menuItemIcon}
                        />
                    }
                >
                    <ScoopText className={styles.menuItemText}>Scoop Admin</ScoopText>
                </ScoopMenuItem>
            )}
            <ScoopMenuItem
                onClick={handleSettings}
                leftSection={
                    <GearIcon size={20} weight="regular" className={styles.menuItemIcon} />
                }
            >
                <ScoopText className={styles.menuItemText}>Account settings</ScoopText>
            </ScoopMenuItem>
            <ScoopMenuItem
                onClick={handleLogout}
                leftSection={
                    <SignOutIcon size={20} weight="regular" className={styles.menuItemIcon} />
                }
            >
                <ScoopText className={styles.menuItemText}>Log out</ScoopText>
            </ScoopMenuItem>
            <ScoopText className={styles.versionBadge}>
                {process.env.REACT_APP_PRODUCT_VERSION}
            </ScoopText>
        </ScoopMenu>
    )
}
